import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  type Unsubscribe,
} from '@firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from '@firebase/storage';
import { db, storage } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { MapOverlay } from '../types';
import { mapDocs } from '@utils/firestore';
import { MapOverlaySchema } from '../schemas/firestoreSchemas';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:mapOverlays');

// Firestore rejects undefined values (e.g. an omitted optional `note`).
// Strip them before every write, same as defensePlansApi.
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

// Only this path shape is accepted by storage.rules for overlays — keep the
// client constructor aligned so a typo never silently hits the bucket and
// gets rejected mid-upload.
const OVERLAY_IMAGE_PATH = /^overlays\/[A-Za-z0-9_-]{1,64}\/image\.jpg$/;

function overlayImagePath(overlayId: string): string {
  return `overlays/${overlayId}/image.jpg`;
}

// Fields the moderator's import/placement flow provides; visibility,
// createdBy and the timestamps are supplied by createOverlay itself.
export interface CreateOverlayInput {
  title: string;
  source: MapOverlay['source'];
  areaId: string;
  imagePath: string;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  corners: MapOverlay['corners'];
  opacity?: number;
  note?: string;
  createdBy: string;
}

export type UpdateOverlayInput = Partial<
  Pick<MapOverlay, 'corners' | 'opacity' | 'title' | 'visibility' | 'note' | 'source'>
>;

export const mapOverlaysApi = {
  // Pre-generates a Firestore doc id, the same shape as defensePlansApi's
  // newPlanId — lets the moderator's flow upload the image to
  // overlays/{id}/image.jpg before the doc itself is written.
  newOverlayId(): string {
    return doc(collection(db, COLLECTIONS.MAP_OVERLAYS)).id;
  },

  // Uploads an already-compressed image blob to overlays/{id}/image.jpg.
  // Mirrors uploadBlob (src/api/storage.ts) rather than reusing it — that
  // helper's ALLOWED_PATHS allowlist only covers posts/avatars/structures
  // paths, and extending it there would let those surfaces' validation
  // drift from this one's.
  async uploadOverlayImage(
    overlayId: string,
    blob: Blob
  ): Promise<{ imagePath: string; imageUrl: string }> {
    const imagePath = overlayImagePath(overlayId);
    if (!OVERLAY_IMAGE_PATH.test(imagePath)) {
      throw new Error(`Invalid overlay image path: ${imagePath}`);
    }
    const storageRef = ref(storage, imagePath);
    const uploadTask = uploadBytesResumable(storageRef, blob, { contentType: 'image/jpeg' });

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        null,
        (error) => reject(error),
        async () => {
          const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ imagePath, imageUrl });
        }
      );
    });
  },

  // Always writes visibility 'draft' — a newly imported overlay starts
  // hidden from everyone but moderators until the placement editor's
  // "Share" action flips it (updateOverlay).
  async createOverlay(id: string, input: CreateOverlayInput): Promise<void> {
    const docRef = doc(db, COLLECTIONS.MAP_OVERLAYS, id);
    await setDoc(
      docRef,
      stripUndefined({
        title: input.title,
        source: input.source,
        areaId: input.areaId,
        imagePath: input.imagePath,
        imageUrl: input.imageUrl,
        imageWidth: input.imageWidth,
        imageHeight: input.imageHeight,
        corners: input.corners,
        opacity: input.opacity ?? 0.6,
        visibility: 'draft' as const,
        note: input.note,
        createdBy: input.createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    );
  },

  async updateOverlay(id: string, patch: UpdateOverlayInput): Promise<void> {
    const docRef = doc(db, COLLECTIONS.MAP_OVERLAYS, id);
    await updateDoc(docRef, stripUndefined({ ...patch, updatedAt: serverTimestamp() }));
  },

  // Deletes the Firestore doc first, then the Storage file — the doc is
  // what every reader/query is keyed on, so an overlay stops appearing on
  // the map even if the storage delete below fails partway. object-not-found
  // is swallowed: the file may already be gone (a retried delete, or an
  // overlay whose image upload never completed).
  async deleteOverlay(overlay: MapOverlay): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.MAP_OVERLAYS, overlay.id));
    try {
      await deleteObject(ref(storage, overlay.imagePath));
    } catch (err) {
      if ((err as { code?: string })?.code !== 'storage/object-not-found') {
        log.warn('deleteOverlay: storage cleanup failed:', err);
      }
    }
  },

  // World-readable set of every shared overlay, for the map's Layers sheet
  // toggle list. Global rather than scoped to one area: the collection is a
  // handful of docs per community, and scoping by area meant a toggled
  // layer vanished the moment the map center panned across the area's
  // bbox. Relies on the composite index (visibility ASC, updatedAt DESC).
  // No isAuthed() gate on the shared read branch, so this also runs signed
  // out.
  subscribeSharedOverlays(
    onNext: (overlays: MapOverlay[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(
      collection(db, COLLECTIONS.MAP_OVERLAYS),
      where('visibility', '==', 'shared'),
      orderBy('updatedAt', 'desc')
    );
    return onSnapshotWithRetry(
      q,
      (snap) => onNext(mapDocs(snap, MapOverlaySchema)),
      (error) => {
        log.error('subscribeSharedOverlays failed:', error);
        onError?.(error);
      }
    );
  },

  // Every overlay (draft and shared), for a moderator's import sheet and
  // the map layer. Global for the same reason as subscribeSharedOverlays
  // above; needs only the single-field updatedAt index (no composite entry
  // required).
  subscribeAllOverlays(
    onNext: (overlays: MapOverlay[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(collection(db, COLLECTIONS.MAP_OVERLAYS), orderBy('updatedAt', 'desc'));
    return onSnapshotWithRetry(
      q,
      (snap) => onNext(mapDocs(snap, MapOverlaySchema)),
      (error) => {
        log.error('subscribeAllOverlays failed:', error);
        onError?.(error);
      }
    );
  },
};
